<script setup lang="ts">
/**
 * « Ce que permet votre don » en carrousel : un palier à la fois ; flèches,
 * compteur « 1 / 3 », glisser (souris, doigt, pavé tactile), boucle infinie
 * s'il y en a plus d'un. Cliquer un palier choisit son montant.
 *
 * Sorti de la carte de don de la page cagnotte (PageCagnotteCarteDon) pour
 * être EXACTEMENT le même dans le formulaire de don intégrable (widget et
 * démo de l'accueil) — Karim 2026-10-13 : « swipeable avec flèches, exactement
 * pareil que sur la cagnotte ».
 */
import { toRef } from "vue";
import DonateImpactOptions, { type ImpactOption } from "./DonateImpactOptions.vue";
import { useCarrouselPaliers } from "./useCarrouselPaliers";

const props = withDefaults(
  defineProps<{
    impacts: ImpactOption[];
    formatAmount: (amount: number) => string;
    primaryColor?: string | null;
    /** « / mois »… pour un don récurrent. */
    periodSuffix?: string | null;
  }>(),
  { primaryColor: null, periodSuffix: null },
);

/** Montant choisi (texte, comme le champ de montant). */
const montant = defineModel<string>("amount", { required: true });

const {
  indexPalier,
  rail,
  anime,
  enGlisse,
  styleRail,
  changerPalier,
  finAnimation,
  debutGlisse,
  pendantGlisse,
  finGlisse,
  bloquerClicApresGlisse,
  roue,
} = useCarrouselPaliers(toRef(props, "impacts"));
</script>

<template>
  <div v-if="impacts.length" class="space-y-2.5">
    <!-- Une seule ligne dans toutes les langues : titre coupé (« … ») plutôt
         que renvoyé à la ligne ; compteur et flèches ne rétrécissent pas. -->
    <div class="flex items-center justify-between gap-3">
      <p class="flex min-w-0 items-center gap-1.5 text-sm text-gray-900" style="font-weight: 600 !important">
        <UIcon name="i-lucide-heart-handshake" class="size-4 shrink-0" />
        <span class="truncate">{{ $t("don.impact.titre") }}</span>
      </p>
      <div v-if="impacts.length > 1" class="flex shrink-0 items-center gap-1.5">
        <span class="text-xs text-gray-400 tabular-nums whitespace-nowrap mr-1">{{ indexPalier + 1 }} / {{ impacts.length }}</span>
        <button
          type="button"
          class="w-8 h-8 rounded-full ring-1 ring-gray-200 bg-white text-(--color-ink) flex items-center justify-center hover:ring-(--color-ink) transition-all"
          :aria-label="$t('don.impact.precedent')"
          @click="changerPalier(-1)"
        >
          <UIcon name="i-lucide-chevron-left" class="size-4" />
        </button>
        <button
          type="button"
          class="w-8 h-8 rounded-full ring-1 ring-gray-200 bg-white text-(--color-ink) flex items-center justify-center hover:ring-(--color-ink) transition-all"
          :aria-label="$t('don.impact.suivant')"
          @click="changerPalier(1)"
        >
          <UIcon name="i-lucide-chevron-right" class="size-4" />
        </button>
      </div>
    </div>
    <!-- Rail : précédent | actuel | suivant -->
    <div
      v-if="impacts.length > 1"
      class="overflow-hidden pt-2.5 -mt-2.5 select-none"
      :class="enGlisse ? 'cursor-grabbing' : 'cursor-grab'"
      style="touch-action: pan-y"
      data-balayage-propre
      @pointerdown="debutGlisse"
      @pointermove="pendantGlisse"
      @pointerup="finGlisse"
      @pointercancel="finGlisse"
      @click.capture="bloquerClicApresGlisse"
      @wheel="roue"
    >
      <div
        class="flex items-start"
        :class="anime ? 'transition-transform duration-300 ease-out' : ''"
        :style="styleRail"
        @transitionend.self="finAnimation"
      >
        <div v-for="diapo in rail" :key="diapo.cle" class="w-full shrink-0">
          <DonateImpactOptions
            v-model:amount="montant"
            :options="[diapo.palier]"
            :format-amount="formatAmount"
            :primary-color="primaryColor"
            :period-suffix="periodSuffix"
            masquer-titre
          />
        </div>
      </div>
    </div>
    <div v-else class="pt-2.5 -mt-2.5">
      <DonateImpactOptions
        v-model:amount="montant"
        :options="impacts"
        :format-amount="formatAmount"
        :primary-color="primaryColor"
        :period-suffix="periodSuffix"
        masquer-titre
      />
    </div>
  </div>
</template>
